import { em } from 'polished'
import PropTypes from 'prop-types'
import React from 'react'

import TextWithCheck from 'components/_special/TextWithCheck'
import TextWithPlus from 'components/_special/TextWithPlus'
import ButtonScrollDown from 'components/ButtonScrollDown'
import List from 'components/List'
import { RU, UK, IE, NL, BE } from 'constants/countries'
import { GOODS_TYPES } from 'constants/sales'
import AddAndGoToCart from 'containers/AddAndGoToCart'
import { LocalizationContext } from 'contexts/Localization'
import { ResizeContext } from 'contexts/Resize'
import { Col, Row } from 'styles/components/layout'
import { H1Small as H1 } from 'styles/components/typography'
import { getActiveDiscount } from 'utils/sales/discounts'

import BAboutFirstBlockEuroBox from './components/BAboutFirstBlockEuroBox'
import BAboutFirstBlockRussianBox from './components/BAboutFirstBlockRussianBox'
import {
  DiscountNameLabel,
  ButtonInner,
  ColStyled,
  CostContainerSaleStyled,
  CostWrapper,
  DiscountLabel,
  FirstBlockWrapperStyled,
  LabelsWrapper,
  LabelText,
  ListStyled,
  ListWrapper,
  RatedText,
  RowStyled,
  SalesContainer,
  StarsBox,
  TrustpilotWrapper,
  Decorations,
  DynamicImageStyled,
} from './styles'
const { discounts } = process.env.localization

const theme = {
  regular: {
    bg: 'catskillWhite',
    button: 'grass',
    textButton: 'white',
    text: 'black',
    listText: 'dark',
    bullet: 'green',
  },
  blackFriday: {
    bg: 'black',
    button: 'white',
    textButton: 'black',
    text: 'white',
    listText: 'white',
    bullet: 'white',
    labelBg: 'yellow',
    labelText: 'black',
  },
  newYear: {
    bg: 'tePapaGreen',
    button: 'white',
    textButton: 'black',
    text: 'white',
    listText: 'white',
    bullet: 'white',
    labelBg: 'monza',
    labelText: 'white',
    decoratingImg: 'biome_newYear',
  },
  highlighted: {
    bg: 'catskillWhite',
    button: 'grass',
    textButton: 'white',
    text: 'black',
    listText: 'dark',
    bullet: 'black',
  },
}

/**
 * BAboutFirstBlock
 *
 * @param {object} props - properties of component
 * @returns {node} BAboutFirstBlock
 */
const BAboutFirstBlock = ({ data, trpReview, secondBlockRef }) => {
  const { locale, paths, dict, sales } = React.useContext(LocalizationContext)
  const { mobile } = React.useContext(ResizeContext)

  const isFreeConsultation = React.useMemo(
    () => [UK, IE, NL, BE].includes(locale),
    [locale],
  )

  const isRu = React.useMemo(() => locale === RU, [locale])
  const isUk = React.useMemo(() => locale === UK, [locale])

  const discountTheme = React.useMemo(() => {
    const { theme } = getActiveDiscount(discounts, locale) || {}
    return theme
  }, [locale])

  const isDifferentElementsOrder = React.useMemo(() => discountTheme || isUk, [
    discountTheme,
    isUk,
  ])

  const { discount } = sales?.goods
    ? sales.goods[GOODS_TYPES.biome]
    : { discount: 0 }

  const colorTheme = discountTheme
    ? theme[discountTheme]
    : isFreeConsultation
    ? theme.highlighted
    : theme.regular

  const listContent = React.useMemo(
    () =>
      mobile
        ? dict.kits[GOODS_TYPES.biome].list_mob.filter(item => !!item)
        : dict.kits[GOODS_TYPES.biome].list.filter(item => !!item),
    [mobile, dict],
  )

  const trustpilotRate = React.useMemo(
    () =>
      !!trpReview &&
      isUk && (
        <TrustpilotWrapper>
          <StarsBox>
            <img
              alt={`TrustScore ${trpReview.score.stars} out of 5`}
              src={`https://cdn.trustpilot.net/brand-assets/4.1.0/stars/stars-${trpReview.score.stars}.svg`}
            />
          </StarsBox>
          <RatedText
            dangerouslySetInnerHTML={{ __html: dict.trustpilot.rated }}
          />
        </TrustpilotWrapper>
      ),
    [trpReview, isUk, dict],
  )

  const isZeroPrice =
    sales?.goods?.[GOODS_TYPES?.biome] &&
    sales?.goods?.[GOODS_TYPES.biome]?.currentPrice === 0 ||
    sales?.goods?.[GOODS_TYPES.biome]?.priceWebsite === 0 ||
    sales?.goods?.[GOODS_TYPES.biome]?.price === 0

  return (
    <FirstBlockWrapperStyled
      bg={colorTheme.bg}
      isExtraBottomIndent={isDifferentElementsOrder}
      textColor={colorTheme.text}
    >
      <RowStyled>
        <ColStyled
          flexGrow={{ xs: 1, lg: 0 }}
          width={{ xs: 1, lg: 8 / 12 }}
          m={{ lg: '0 auto' }}
          textAlign={{ xs: 'center' }}
        >
          <H1
            dangerouslySetInnerHTML={{
              __html: isFreeConsultation ? data.title_consultation : data.title,
            }}
          />
        </ColStyled>
      </RowStyled>

      <RowStyled>
        <ColStyled width={{ xs: 1, md: 6 / 12 }} pt={{ md: em(20) }}>
          {isRu ? (
            <BAboutFirstBlockRussianBox
              discount={discountTheme ? discount : 0}
              labelTheme={{
                bg: colorTheme.labelBg,
                text: colorTheme.labelText,
              }}
              {...{ path: `${paths.cdn_global}/boxes` }}
            />
          ) : (
            <BAboutFirstBlockEuroBox
              {...{ path: `${paths.cdn_global}/boxes` }}
            />
          )}
          {!!colorTheme.decoratingImg && (
            <Decorations>
              <DynamicImageStyled
                image={`${paths.shared}/decorations/${colorTheme.decoratingImg}`}
                imageType="png"
                imageSizes={{ xs: 260, md: 287, lg: 470 }}
                fitOption="contain"
                namesByBreakpoints
                alt={'Decorations'}
              />
            </Decorations>
          )}
        </ColStyled>

        <Col width={{ xs: 1, md: 6 / 12 }}>
          <Row>
            <ColStyled
              width={{ xs: 1 }}
              pt={{ xs: em(isUk ? 20 : 32), md: em(32) }}
              order={{ xs: isDifferentElementsOrder ? 1 : 0, md: 1 }}
            >
              <SalesContainer>
                {!!discountTheme && discount > 0 && !isZeroPrice && (
                  <LabelsWrapper>
                    <DiscountLabel
                      bg={colorTheme.labelBg}
                      textColor={colorTheme.labelText}
                    >
                      <LabelText
                        dangerouslySetInnerHTML={{ __html: `-${discount}%` }}
                      />
                    </DiscountLabel>
                    <DiscountNameLabel>
                      <LabelText
                        dangerouslySetInnerHTML={{
                          __html: dict[discountTheme],
                        }}
                      />
                    </DiscountNameLabel>
                  </LabelsWrapper>
                )}
                <CostWrapper isMobileLeftAlign={!!discountTheme}>
                  {!isZeroPrice && (
                    <CostContainerSaleStyled
                      productType={GOODS_TYPES.biome}
                      isMobileCenter={!discountTheme}
                      crossedSize={mobile ? 'base' : 'medium'}
                      isWhite={!!discountTheme}
                    />
                  )}

                  <AddAndGoToCart
                    goods={[
                      {
                        type: GOODS_TYPES.biome,
                        isSubscription: false,
                      },
                    ]}
                    buttonProps={{
                      bgClassName: `bg--${colorTheme.button}`,
                      textClassName: `text--${colorTheme.textButton}`,
                      withTopMarginOnMobile: true,
                      isDisabled: isZeroPrice,
                    }}
                    forceMultipleAdd={true}
                  >
                    <ButtonInner
                      as="span"
                      dangerouslySetInnerHTML={{
                        __html: sales.withoutSales || isZeroPrice
                          ? dict.buttons.out_of_stock
                          : isRu
                          ? dict.buttons.buy
                          : dict.buttons.add_to_cart,
                      }}
                    />
                  </AddAndGoToCart>
                </CostWrapper>
                {!mobile && trustpilotRate}
              </SalesContainer>
            </ColStyled>

            <ColStyled
              width={{ xs: 1 }}
              order={{ xs: isDifferentElementsOrder ? 0 : 1, md: 0 }}
              pt={{ xs: em(isDifferentElementsOrder ? 20 : 40), md: 0 }}
              textAlign={{ xs: 'center', md: 'left' }}
            >
              <ListWrapper>
                {mobile && trustpilotRate}
                <ListStyled
                  leftAlignment="text"
                  textColor={colorTheme.listText}
                >
                  {listContent.map((item, index) => (
                    <List.Item key={index} bulletColor={colorTheme.bullet}>
                      {item}
                    </List.Item>
                  ))}
                </ListStyled>
                <TextWithPlus
                  text={dict.kits[GOODS_TYPES.biome].consultation_free}
                />
                {isFreeConsultation && (
                  <TextWithCheck text={data.description} />
                )}
              </ListWrapper>
            </ColStyled>
          </Row>
        </Col>
      </RowStyled>

      {!isDifferentElementsOrder && (
        <Row>
          <ColStyled width={{ xs: 1 }} pt={{ xs: em(40) }}>
            <ButtonScrollDown
              title={data.go_to_site}
              refToScroll={secondBlockRef}
            />
          </ColStyled>
        </Row>
      )}
    </FirstBlockWrapperStyled>
  )
}

BAboutFirstBlock.propTypes = {
  data: PropTypes.object.isRequired,
  secondBlockRef: PropTypes.object.isRequired,
  trpReview: PropTypes.shape({
    score: PropTypes.shape({
      stars: PropTypes.number,
      trustScore: PropTypes.number,
    }),
    numberOfReviews: PropTypes.shape({
      total: PropTypes.number,
    }),
  }),
}

BAboutFirstBlock.defaultProps = {}

export default BAboutFirstBlock
